import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Autocomplete from '@mui/joy/Autocomplete';
import CircularProgress from '@mui/joy/CircularProgress';
import _ from 'lodash';
import { getRoles } from '../API/authentication';
import { fetchAllPages } from '../utils/pagination';
import { PERMISSIONS } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const defaultControlSx = {
  backgroundColor: '#f7f9fc',
  borderRadius: '8px',
  padding: '4px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  '&:hover': { borderColor: '#5f09c2' },
  '&:focus': { borderColor: '#5f09c2', outline: 'none', boxShadow: '0 0 6px rgba(0, 123, 255, 0.5)' },
  '&[disabled]': { backgroundColor: '#e9ecef', color: '#6c757d', cursor: 'not-allowed' },
};

function getRoleLabel(role) {
  if (!role) return '';
  return role.display_name || role.name || (role.id != null ? `#${role.id}` : '');
}

export default function RoleOptionList({
  onChange,
  required,
  value,
  disabled = false,
  label,
  initialOption = null,
  errorMessage,
}) {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const canListRoles = hasPermission(PERMISSIONS.ROLES_LIST);
  const resolvedLabel = label ?? t('settingsPages.userRoles.labelRole');
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const fetchRoles = async (searchTerm = '') => {
    if (!canListRoles) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const roles = await fetchAllPages(
        (page, pageSize) => getRoles({
          page,
          page_size: pageSize,
          is_active: true,
          ...(searchTerm ? { search: searchTerm } : {}),
        }),
      );
      setOptions(roles);
    } catch (error) {
      console.error('Failed to fetch roles:', error.message);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchRoles = useCallback(_.debounce(fetchRoles, 300), [canListRoles]);

  useEffect(() => {
    if (inputValue) {
      debouncedFetchRoles(inputValue);
    }
  }, [inputValue, debouncedFetchRoles]);

  useEffect(() => {
    if (open) {
      fetchRoles(inputValue);
    }
  }, [open]);

  useEffect(() => {
    if (!value) {
      setSelectedRole(null);
      return;
    }
    const fromOptions = options.find((role) => role.id === value);
    if (fromOptions) {
      setSelectedRole(fromOptions);
      return;
    }
    if (initialOption && initialOption.id === value) {
      setSelectedRole(initialOption);
      return;
    }
    setSelectedRole((prev) =>
      prev?.id === value ? prev : { id: value, display_name: `#${value}`, name: `#${value}` }
    );
  }, [value, options, initialOption]);

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    if (!newInputValue) {
      fetchRoles();
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        marginBottom: 2,
        position: 'relative',
        '&:focus-within': {
          boxShadow: '0 0 8px rgba(0, 128, 255, 0.3)',
          borderRadius: defaultControlSx.borderRadius || '8px',
        },
      }}
    >
      <FormLabel
        sx={{
          fontWeight: 600,
          color: errorMessage ? 'danger.500' : '#555',
          marginBottom: '6px',
          fontSize: '14px',
        }}
      >
        {required ? `${resolvedLabel} *` : resolvedLabel}
      </FormLabel>
      <Autocomplete
        onInputChange={handleInputChange}
        value={selectedRole}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        placeholder={t('widgets.searchRoles', { defaultValue: t('settingsPages.userRoles.selectRole') })}
        options={Array.isArray(options) ? options : []}
        loading={loading}
        disabled={disabled || !canListRoles}
        onChange={(event, role) => {
          setSelectedRole(role);
          onChange(event, role?.id ?? '');
        }}
        isOptionEqualToValue={(option, selected) => option?.id === selected?.id}
        getOptionLabel={getRoleLabel}
        noOptionsText={
          !canListRoles ? (
            <span>
              {t('permissions.noPermission', {
                defaultValue: 'You do not have permission to perform this action.',
              })}
            </span>
          ) : (
            t('widgets.noRolesFound', { defaultValue: 'No roles found' })
          )
        }
        loadingText={t('actions.loading')}
        filterOptions={(opts) => opts}
        endDecorator={
          loading ? <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} /> : null
        }
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          ...defaultControlSx,
          ...(errorMessage
            ? {
                borderColor: 'var(--joy-palette-danger-500, #c41c1c)',
                boxShadow: '0 0 0 1px var(--joy-palette-danger-500, #c41c1c)',
              }
            : {}),
        }}
      />
      {errorMessage ? (
        <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>{errorMessage}</FormHelperText>
      ) : null}
    </Box>
  );
}
